import { useLocation, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Home, ShieldAlert } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import SEO from '@/components/SEO';
import GridBackground from '@/components/GridBackground';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function PageNotFound() {
  const location = useLocation();
  const pageName = location.pathname.substring(1);
  const es = /^\/(es|mx)(\/|$)/.test(location.pathname);

  const { data: authData, isFetched } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      try {
        const user = await base44.auth.me();
        return { user, isAuthenticated: true };
      } catch {
        return { user: null, isAuthenticated: false };
      }
    }
  });

  return (
    <div className="min-h-screen bg-background text-foreground relative flex flex-col">
      <SEO title={es ? 'Página no encontrada' : 'Page Not Found'} description={es ? 'La página que buscas no existe en Jotunheims.' : "The page you're looking for doesn't exist on Jotunheims."} path={location.pathname} lang={es ? 'es' : 'en'} noindex />
      <GridBackground />
      <Navbar />

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 pt-32 pb-24">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <ShieldAlert className="w-8 h-8 text-primary" />
            </div>
          </div>

          <div className="space-y-2">
            <h1 className="text-7xl font-inter font-black text-primary/30">404</h1>
            <div className="h-px w-16 bg-primary/40 mx-auto" />
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-inter font-bold text-foreground">{es ? 'Página no encontrada' : 'Page Not Found'}</h2>
            <p className="text-muted-foreground font-inter leading-relaxed">
              {es ? 'La página ' : 'The page '}<span className="font-medium text-foreground">"/{pageName}"</span>{es ? ' no existe o se ha movido.' : " doesn't exist or may have been moved."}
            </p>
          </div>

          {isFetched && authData.isAuthenticated && authData.user?.role === 'admin' && (
            <div className="p-4 bg-secondary/30 rounded-xl border border-border/50 text-left">
              <p className="text-sm font-inter font-medium text-foreground mb-1">Admin Note</p>
              <p className="text-sm text-muted-foreground font-inter leading-relaxed">
                This could mean the AI hasn't implemented this page yet. Ask it to implement it in the chat.
              </p>
            </div>
          )}

          <div className="pt-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-inter font-semibold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/25"
            >
              <Home className="w-4 h-4" />
              {es ? 'Volver al inicio' : 'Back to Home'}
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}